/* eslint-disable @typescript-eslint/ban-ts-comment */

import { ElementProps } from '@/components/sections/builder/types';

import { POSITION, POSITION_DETAIL } from '@/components/sections/builder/constants/position';
import {
  ContentCardInfoType,
  ElementDataType,
} from '@/components/sections/builder/elements/body/slideshow/types';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, removeHTMLTags } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { Images } from 'lucide-react';
import InputLink from '@/components/sections/builder/settings/input-link';
import Link from 'next/link';
import useMediaQuery from '@/hooks/useMediaQuery';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import DynamicImage from '@/components/commons/dynamic-image';

const defaultData: ContentCardInfoType = {
  name: 'Slide',
  heading: 'Image slide',
  subheading: `Tell your brand's story through images`,
  buttonLabel: 'Button label',
  buttonLink: '#',
  headingSize: '0',
  isOutlineButton: false,
  desktopContentPosition: 'middleCenter',
  desktopContentAlignment: 'center',
  imgOverlayOpacity: 0,
  colorSchemeBackround: 'background1',
  mobileLayout: 'center',
  img: '/assets/temporary-banner.png',
  imgLink: '#',
  mobileMinWidth: 720,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ContentCardInfoType;
  const parentData = (element.parentData || defaultData) as ElementDataType;
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  const [mediaLoaded, setMediaLoaded] = useState(false);

  const alignClass = React.useMemo(() => {
    if (data.desktopContentAlignment === 'left') {
      return 'lg:text-left';
    }
    if (data.desktopContentAlignment === 'right') {
      return 'lg:text-right';
    }
    return 'lg:text-center';
  }, [data.desktopContentAlignment]);

  const alignClassMobile = React.useMemo(() => {
    if (data.mobileLayout === 'left') {
      return 'text-left';
    }
    if (data.mobileLayout === 'right') {
      return 'text-right';
    }
    return 'text-center';
  }, [data.mobileLayout]);

  const imgUrl = useMediaQuery(data.mobileMinWidth) && data.mobileImg ? data.mobileImg : data.img;

  useEffect(() => {
    const elements = document.getElementsByClassName(
      `slideshow-item-${element.parentId}`
    ) as HTMLCollectionOf<HTMLElement>;

    if (elements.length > 0) {
      let firstElement;
      for (let i = 0; i < elements.length; i++) {
        if (!firstElement && elements[i]?.tagName !== 'VIDEO') {
          firstElement = elements[i];
          break;
        }
      }

      if (!firstElement) firstElement = elements[0];

      if (firstElement.tagName === 'VIDEO') {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          const naturalHeight = video.videoHeight;
          const naturalWidth = video.videoWidth;
          const isMobile = windowWidth <= 768;
          if (!!naturalHeight && parentData?.slideHeight === 'adapt') {
            const wrappers = document.getElementsByClassName(
              `slideshow-wrapper-${element.parentId}`
            );
            for (let i = 0; i < wrappers.length; i++) {
              const wrapperWidth = wrappers[i]?.getBoundingClientRect()?.width || 0;
              const wrapperHeight = (naturalHeight / naturalWidth) * wrapperWidth;
              const wrapper = wrappers[i] as HTMLElement;
              wrapper.style.height = `${isMobile ? 390 : wrapperHeight}px`;
            }
          }

          video.src = '';
        };

        video.src = firstElement.getAttribute('src') as string;
      } else {
        const firstImage = new Image();
        firstImage.src = firstElement.getAttribute('src') as string;
        firstImage.onload = () => {
          const { naturalHeight, naturalWidth } = firstImage;
          const wrappers = document.getElementsByClassName(`slideshow-wrapper-${element.parentId}`);
          for (let i = 0; i < wrappers.length; i++) {
            if (!!naturalHeight && parentData?.slideHeight === 'adapt') {
              const wrapperWidth = wrappers[i]?.getBoundingClientRect()?.width || 0;
              const wrapperHeight = (naturalHeight / naturalWidth) * wrapperWidth;
              const wrapper = wrappers[i] as HTMLElement;
              wrapper.style.height = `${wrapperHeight}px`;
            } else {
              const wrapper = wrappers[i] as HTMLElement;
              wrapper.style.height = '';
            }
          }
        };
      }
    }
  }, [windowWidth, mediaLoaded, parentData.slideHeight, parentData, imgUrl]);

  const position = POSITION_DETAIL[data?.desktopContentPosition] || POSITION_DETAIL['middleCenter'];

  return (
    <>
      <Link href={data.imgLink || '#'}>
        <div
          className={cn(
            'slideshow-content relative h-full w-full bg-transparent ',
            data.imgOverlayOpacity > 0 && 'p-0 lg:bg-black'
          )}
        >
          <div>
            <div
              className={cn(
                'relative w-full object-cover object-center',
                parentData?.slideHeight === 'small' && 'h-[280px] md:h-[420px]',
                parentData?.slideHeight === 'medium' && 'h-[340px] md:h-[560px]',
                parentData?.slideHeight === 'large' && 'h-[390px] md:h-[720px]',
                `slideshow-wrapper-${element.parentId}`
              )}
            >
              {data.video ? (
                <video
                  key={removeHTMLTags(data.video)}
                  id={`slideshow-video-${element.id}`}
                  loop
                  autoPlay
                  muted
                  src={removeHTMLTags(data.video)}
                  onLoad={() => setMediaLoaded(true)}
                  className={cn(
                    '!h-full !w-full object-cover object-center',
                    `slideshow-item-${element.parentId}`
                  )}
                />
              ) : (
                <div className='!relative !h-full !w-full overflow-hidden'>
                  <DynamicImage
                    key={imgUrl}
                    className={clsx(
                      '!relative !h-full !w-full object-cover object-center',
                      `slideshow-item-${element.parentId}`,
                      parentData.animation === 'ambientMovement' && 'animate-zoomSlideDown'
                    )}
                    width={1920}
                    height={1080}
                    sizes='100vw'
                    src={imgUrl as string}
                    alt='image'
                    onLoadingComplete={() => setMediaLoaded(true)}
                    style={{
                      width: '100%',
                      height: 'auto',
                      opacity: `${100 - data.imgOverlayOpacity}%`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className={cn(
              `slideshow-card-layout static left-0 top-0 w-full p-0 md:absolute md:h-full lg:p-6`,
              !parentData.showContentBelowImagesOnMobile && 'absolute h-full'
            )}
          >
            <div className={cn('slideshow-card-wrapper w-full md:w-fit')}>
              <div
                className={cn(
                  'slideshow-card bg-transparent p-6 py-10 lg:rounded lg:px-12 lg:py-16',
                  alignClassMobile,
                  alignClass
                )}
              >
                <h3
                  className='leading-tight'
                  style={{
                    fontSize: `${data.headingSize}px`,
                    color: data?.colorScheme?.text || '#52535B',
                  }}
                  dangerouslySetInnerHTML={{ __html: data.heading }}
                />
                <p
                  className='mt-4 mb-8'
                  style={{ color: data?.colorScheme?.text || '#52535B' }}
                  dangerouslySetInnerHTML={{ __html: data.subheading }}
                />
                {data.buttonLabel && (
                  <Link href={data.buttonLink || '#'}>
                    <Button
                      className='theme_button button'
                      style={{
                        // @ts-ignore
                        cssText: `;
                    background-color: ${
                      data.isOutlineButton
                        ? 'transparent'
                        : data?.colorScheme?.btnSolidBackgroud || 'white'
                    } !important;
                    color: ${
                      data.isOutlineButton
                        ? data?.colorScheme?.btnOutline || 'white'
                        : data?.colorScheme?.btnSolidlabel || '#52535B'
                    };
                    outline-color: ${
                      data.isOutlineButton ? data?.colorScheme?.btnOutline || '#52535B' : 'none'
                    } !important;
                    `,
                      }}
                    >
                      {data.buttonLabel}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
      <style>
        {`
          .slideshow-card-wrapper {
            display: flex;
          }
          .slideshow-card {
            max-width: 45.5rem;
            width: 100%;
          }
          .slideshow-image {
            display: block;
            max-width: 100%;
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            object-fit: cover;
            object-position: center center;
            transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }
          .slideshow-card-layout {
            display: flex;
            justify-content: ${position.content};
            align-items: ${position.align};
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Images;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ContentCardInfoType;

  return (
    <div className='w-full max-w-md'>
      <div className='mb-3'>Video url</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            video: value,
          })
        }
        value={data.video}
        className='w-full mb-4 max-w-64'
        output='text'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4'>Supported formats: .mp4, .webm, .ogv</div>
      <ImageSetting
        value={data?.img}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            img: val,
          });
        }}
      />
      <div className='mt-4 mb-2'>Mobile image (optional)</div>
      <ImageSetting
        value={data?.mobileImg}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            mobileImg: val,
          });
        }}
      />
      <InputSlider
        label='Device under this size use mobile image'
        value={data.mobileMinWidth || 0}
        max={1440}
        min={0}
        rightContent={<div>px</div>}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            mobileMinWidth: val,
          })
        }
      />
      <div className='mt-4 mb-4 font-bold'>Image Link</div>
      <InputLink
        name='url_path_img'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            imgLink: value,
          });
        }}
        value={data.imgLink as string}
      />
      <div className='my-3'>Heading</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            heading: value,
          })
        }
        value={data.heading}
        className='w-full mb-4'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mt-4 mb-2'>Heading Size</div>
      <InputTab
        defaultValue={data.headingSize}
        options={[
          { label: 'Small', value: '20' },
          { label: 'Medium', value: '30' },
          { label: 'Large', value: '40' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            headingSize: v,
          })
        }
      />
      <div className='mt-4 mb-2'>Subheading</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            subheading: value,
          })
        }
        value={data.subheading}
        className='w-full'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mt-4 mb-2'>Button Label</div>
      <Input
        onChange={(e) =>
          builder.setChildData(element.parentId as string, element.id, {
            buttonLabel: e.target.value,
          })
        }
        name='buttonLabel'
        type='text'
        size={100}
        defaultValue={data.buttonLabel}
        className='w-full'
      />
      <div className='mt-4 mb-2'>Button Link</div>
      <InputLink
        name='url_path'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            buttonLink: value,
          });
        }}
        value={data.buttonLink as string}
      />
      <div className='flex items-center mt-4'>
        <Checkbox
          checked={data.isOutlineButton}
          onCheckedChange={(value) =>
            builder.setChildData(element.parentId as string, element.id, {
              isOutlineButton: !!value,
            })
          }
          className='mr-4'
        />
        <p>Use outline button style</p>
      </div>
      <div className='mb-2 text-sm md:mt-4'>Desktop content position</div>
      <div className='mb-4'>
        <Select
          name='desktopContentPosition'
          value={data.desktopContentPosition}
          onValueChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              desktopContentPosition: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {POSITION.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mt-4 mb-2'>Desktop content alignment</div>
      <InputTab
        defaultValue={data.desktopContentAlignment}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            desktopContentAlignment: v,
          })
        }
      />
      <InputSlider
        label='Image overlay opacity'
        value={data.imgOverlayOpacity}
        max={100}
        min={0}
        rightContent={<div>%</div>}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            imgOverlayOpacity: val,
          })
        }
      />
      <div className='mt-4 mb-2'>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setChildData(element.parentId as string, element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <div className='mt-4 mb-2'>Mobile content alignment</div>
      <InputTab
        defaultValue={data.mobileLayout}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            mobileLayout: v,
          })
        }
      />
    </div>
  );
}

const Slide = {
  type: 'slide',
  group: 'slideshow',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Slide;
